import React, { createContext, useContext, useState, ReactNode } from "react";
import { ChevronDown, ChevronRight, LogOut, Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

//assets
import Logo from "../../Assets/Images/logo.png";
import LogoMini from "../../Assets/Images/logo.png";
import { Col, message, Popconfirm, Row } from "antd";
import { postRequest } from "../../Utils/ApiCalls";
import {
  formatString,
  getAccountName,
  getUserRole,
  navigateTo,
  version,
  versionShort,
} from "../../Utils/Common";

interface SidebarContextType {
  expanded: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProps {
  children?: ReactNode;
}

interface NavbarItemProps {
  icon: React.ReactNode | null;
  text: string;
  active?: boolean;
  alert?: boolean;
  fontSize?: string;
  url?: string;
  subMenu?: React.ReactNode;
  isSubMenu?: boolean;
  onClick?: () => void;
}

export default function SidebarConf({ children }: SidebarProps) {
  const [expanded, setExpanded] = useState(false);
  async function logout() {
    // const response = await postRequest(`logout`, {}, true);
    // if (response.success === true) {
    //   message.success(
    //     "Logged out successfully. You will now be redirected to the login page."
    //   );
    //   localStorage.clear();
    //   setTimeout(() => {
    //     navigateTo("/");
    //   }, 2000);
    // } else {
    //   message.error("Something went wrong. Please try again later.");
    // }
    navigateTo("/");
  }
  return (
    <div className="h-screen sm:w-fit relative">
      {/* NAVBAR FOR SMALL SCREENS */}
      <nav className="w-screen sm:hidden p-4 bg-primary absolute z-[10000]">
        <div className="flex justify-between">
          <img src={Logo} alt="" className="h-6" />
          <Menu
            className="text-white"
            onClick={() => setExpanded((prev) => !prev)}
          />
        </div>
      </nav>
      {expanded && (
        <nav className="w-screen h-screen z-[2000] sm:hidden p-4 bg-primary absolute top-0">
          <Row>
            <Col xs={24} className="flex">
              <img src={Logo} alt="" className="h-6" />
              <X
                className="text-pageBg ms-auto"
                onClick={() => setExpanded(false)}
              />
            </Col>
            <Col xs={24} className="mt-5">
              <SidebarContext.Provider value={{ expanded }}>
                <ul className="flex-1 sm:px-3 w-full h-full">{children}</ul>
              </SidebarContext.Provider>
            </Col>
            <Col className="mt-3" span={24}>
              <div
                className="px-3 rounded-md cursor-pointer bg-red-400 py-3 text-white flex items-center"
                onClick={() => logout()}
              >
                <LogOut className={`${!expanded ? "mx-auto" : ""}`} size={20} />
                <div
                  className={`
            flex justify-between items-center
            overflow-hidden  transition-all ${expanded ? "w-32 ml-3" : "w-0"}
        `}
                >
                  <p className="m-0">Logout</p>
                </div>
              </div>
            </Col>
          </Row>
        </nav>
      )}

      {/* NAVBAR FOR LARGE SCREENS */}
      <nav
        className="h-full flex-col bg-primary border-r shadow-md hidden sm:flex relative"
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
      >
        <div className="p-4 pb-2 flex justify-center items-center flex-col">
          <img
            onClick={() => setExpanded((curr) => !curr)}
            src={expanded ? Logo : LogoMini}
            className={`h-10 overflow-hidden transition-all cursor-pointer duration-400 my-5 bg-primary rounded-full object-cover ${
              expanded ? "w-[7.5rem] h-[115px]" : "w-[45px] h-[35px]"
            }`}
            alt=""
          />
          <span className="text-xs text-white pb-1">
            {expanded ? version : versionShort}
          </span>
        </div>

        <SidebarContext.Provider value={{ expanded }}>
          <ul className="flex-1 px-3 overflow-scroll">{children}</ul>
        </SidebarContext.Provider>

        <Popconfirm
          title="Are you sure you want to logout?"
          onConfirm={() => logout()}
          okText="Yes"
          cancelText="No"
        >
          <div className="m-3 p-2 rounded-md cursor-pointer text-white flex items-center hover:bg-pageBg hover:text-white">
            <LogOut className={`${!expanded ? "mx-auto" : ""}`} size={20} />
            <div
              className={`
            flex justify-between items-center
            overflow-hidden  transition-all ${expanded ? "w-32 ml-3" : "w-0"}
        `}
            >
              <p className="m-0">Logout</p>
            </div>
          </div>
        </Popconfirm>

        <div className="border-t border-t-gray-200 flex p-3">
          <img
            src="https://img.freepik.com/free-vector/blue-circle-with-white-user_78370-4707.jpg?t=st=1742982778~exp=1742986378~hmac=a8caf0bfb24dad130d863926d64a909931402a5a4b46cdb86f48af543874877f&w=1380"
            alt=""
            // className="w-12 h-auto rounded-md"
            className="w-12 h-12 rounded-full object-cover"
          />
          <div
            className={`
              flex justify-between items-center
              overflow-hidden transition-all ${
                expanded ? "w-32 ml-3" : "hidden"
              }
          `}
          >
            <div className="leading-4  flex flex-col items-center w-full">
              <span className="font-semibold text-white m-0 w-full">
                {getAccountName("full_name")}
              </span>
              <span className="text-xs text-gray-400 m-0 w-full">
                {formatString(getUserRole())}
              </span>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}

export function NavbarItem({
  icon,
  text,
  fontSize,
  active = false,
  alert = false,
  url,
  subMenu,
  isSubMenu = false, // You can remove this if it's no longer needed
  onClick,
}: NavbarItemProps) {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("SidebarContext must be used within a SidebarConf");
  }
  const { expanded } = context;
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    // If there's a submenu, toggle it open/closed
    if (subMenu) {
      setIsOpen((prev) => !prev);
    }

    // If there's a URL, navigate to it
    if (url && !subMenu) {
      if (event.metaKey || event.ctrlKey) {
        window.open(url, "_blank");
      } else {
        navigate(url);
      }
    }

    // Optional additional onClick handler
    if (onClick) {
      onClick();
    }
  };

  return (
    <li
      className={`
        relative mb-1 flex flex-col cursor-pointer transition-colors group ${fontSize}
      `}
      onClick={handleClick}
    >
      <div
        className={`text-white hover:text-white rounded-md flex flex-row items-center py-2 px-3 ${
          active ? "bg-pageBg text-white" : "sm:hover:bg-pageBg"
        } ${isSubMenu ? "pl-6" : ""}`}
      >
        {icon && (
          <div className="icon-container">
            {typeof icon === "string" ||
            (typeof icon === "object" && "icon" in icon) ? (
              // Render FontAwesome or Lucide icon (assuming it’s a React component)
              <span className="icon">{icon}</span>
            ) : (
              // Render SVG or other custom components
              <span className="icon">{icon}</span>
            )}
          </div>
        )}

        <span
          className={`overflow-visible transition-all ${
            expanded ? "w-32 ml-3 inline" : "w-0 hidden"
          }`}
        >
          {text}
        </span>
        {subMenu && (
          <span
            className={`overflow-hidden transition-all ms-auto ${
              expanded ? "inline" : "hidden"
            }`}
          >
            {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </span>
        )}
        {alert && (
          <div
            className={`absolute right-2 w-2 h-2 rounded-full bg-indigo-400 transition-all ${
              expanded ? "visible top-1" : "visible top-2"
            }`}
          />
        )}
      </div>

      {!expanded && (
        <div
          className={`
          absolute left-full rounded-md px-2 py-1 ml-6 mt-1
          bg-yellow-200 text-primary text-sm z-[1000]
          invisible opacity-20 -translate-x-3 transition-all
          group-hover:visible group-hover:opacity-100 group-hover:translate-x-0
      `}
        >
          {text}
        </div>
      )}

      {/* Render subMenu if it's open */}
      {subMenu && isOpen && (
        <div
          className={`sm:pl-6 mt-1 ${expanded ? "block" : "hidden"}`}
          style={{ zIndex: 10 }}
        >
          {subMenu}
        </div>
      )}
    </li>
  );
}
